import { ChangeDetectionStrategy, Component, ElementRef, HostListener, ViewEncapsulation, afterNextRender, computed, effect, input, output, signal, viewChild } from '@angular/core';
import { CdkDragDrop, CdkDrag, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { RadioComponent } from '@workbuddy/ui/v2/fields/radio';
import { CheckboxComponent } from '@workbuddy/ui/v2/fields/checkbox';
import { ButtonComponent } from '@workbuddy/ui/v2/components/button';

import type { AIInterviewAnswer } from '@jms/model';
import type { CardAction, InterviewQuestion, InterviewCardData, InterviewQuestionOption, ShortcutHint } from '../../agent.types';

@Component({
  selector: 'agent-question-card',
  standalone: true,
  imports: [CdkDrag, CdkDropList, RadioComponent, CheckboxComponent, ButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './question.card.component.html',
  styleUrls: ['./question.card.component.less'],
})
export class AgentQuestionCardComponent {

  constructor() {

    afterNextRender(() => this.cardContainerRef()?.nativeElement.focus());

    effect(() => {
      const q = this.$currentQuestion();
      if (!q) return;
      if (q.mode === 'rank' && !this.$rankedOptions().length) {
        this.$rankedOptions.set([...q.options]);
      }
    });
  }

  public $data = input.required<InterviewCardData>({ alias: 'data' });
  public $disabled = input<boolean>(false, { alias: 'disabled' });

  public submit = output<{ action: CardAction; answers: AIInterviewAnswer[] }>();
  public cancel = output<void>();

  /** Zero-based index of the currently displayed question */
  public $currentIndex = signal<number>(0);

  /** Captured answers per question index — single source of truth across navigation */
  private $answers = signal<Map<number, AIInterviewAnswer>>(new Map());

  /** Transient state for the current question — hydrated on index change */
  public $selectedIndex = signal<number | null>(null);
  public $checkedIndices = signal<Set<number>>(new Set());
  public $rankedOptions = signal<InterviewQuestionOption[]>([]);
  public $customText = signal<string>('');
  public $freetextSelected = signal<boolean>(false);
  protected $focusedIndex = signal<number>(0);

  public $currentQuestion = computed<InterviewQuestion>(() => this.$data().questions[this.$currentIndex()]);
  public $totalQuestions = computed<number>(() => this.$data().questions.length);
  public $isFirst = computed<boolean>(() => this.$currentIndex() === 0);
  public $isLast = computed<boolean>(() => this.$currentIndex() === this.$totalQuestions() - 1);

  /** Whether a freetext row is rendered for the current question */
  public $freetextAllowed = computed<boolean>(() => {
    const q = this.$currentQuestion();
    return q.mode !== 'rank' && q.allowFreetext !== false;
  });

  /** Keyboard focus index for the freetext row (one past last numbered option) */
  public $freetextRowIndex = computed<number>(() => this.$currentQuestion().options.length);

  /** Whether the current question has enough input to advance/submit */
  public $canSend = computed<boolean>(() => {
    const q = this.$currentQuestion();
    if (q.mode === 'rank') return this.$rankedOptions().length > 0;
    if (q.mode === 'single') {
      return this.$freetextSelected() ? !!this.$customText().trim() : this.$selectedIndex() !== null;
    }
    if (q.mode === 'multiple') {
      return this.$checkedIndices().size > 0 || !!this.$customText().trim();
    }
    return false;
  });

  /** Count shown in multi-mode footer */
  public $selectionCount = computed<number>(() => this.$checkedIndices().size + (this.$customText().trim() ? 1 : 0));

  /** Shortcut hints for the shared hints bar */
  public readonly $hints = computed<ShortcutHint[]>(() => AgentQuestionCardComponent.hintsForMode(this.$currentQuestion().mode, this.$isLast()));

  /** Static mode→hints derivation — usable without an instance */
  public static hintsForMode(mode: InterviewQuestion['mode'], isLast: boolean): ShortcutHint[] {

    const mod = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent) ? '⌘' : 'Ctrl';
    const submitLabel = isLast ? 'to submit' : 'to continue';

    if (mode === 'rank') {
      return [
        { text: 'Drag to reorder' },
        { text: `${mod}-Enter ${submitLabel}` },
        { text: 'Esc to skip' },
      ];
    }
    const hints: ShortcutHint[] = [
      { text: '↑↓ to navigate' },
      { text: 'Enter to select' },
    ];
    if (mode === 'multiple') {
      hints.push({ text: `${mod}-Enter ${submitLabel}` });
    }
    hints.push({ text: 'Esc to skip' });
    return hints;
  }

  private readonly cardContainerRef = viewChild<ElementRef<HTMLElement>>('cardContainer');

  // ─── Selection handlers ───────────────────────────────────────────────────

  public onRadioSelect(index: number): void {

    this.$selectedIndex.set(index);
    this.$freetextSelected.set(false);
    this.$customText.set('');
    this.$focusedIndex.set(index);
  }

  public onCheckboxToggle(index: number): void {

    const current = new Set(this.$checkedIndices());
    if (current.has(index)) current.delete(index);
    else current.add(index);
    this.$checkedIndices.set(current);
    this.$focusedIndex.set(index);
  }

  public onFreetextInput(value: string): void {

    this.$customText.set(value);
    const mode = this.$currentQuestion().mode;
    if (mode === 'single') {
      if (value.trim()) {
        this.$freetextSelected.set(true);
        this.$selectedIndex.set(null);
      } else {
        this.$freetextSelected.set(false);
      }
    }
  }

  public onFreetextFocus(): void {

    this.$focusedIndex.set(this.$freetextRowIndex());
    if (this.$currentQuestion().mode === 'single' && this.$customText().trim()) {
      this.$freetextSelected.set(true);
    }
  }

  public onSelectFreetextRow(): void {

    this.$selectedIndex.set(null);
    this.$freetextSelected.set(true);
    this.$focusedIndex.set(this.$freetextRowIndex());
  }

  public onDrop(event: CdkDragDrop<InterviewQuestionOption[]>): void {

    const items = [...this.$rankedOptions()];
    moveItemInArray(items, event.previousIndex, event.currentIndex);
    this.$rankedOptions.set(items);
  }

  // ─── Navigation ───────────────────────────────────────────────────────────

  public goNext(): void {

    if (this.$disabled()) return;
    this.captureCurrent();
    if (this.$isLast()) {
      this.submitAll();
      return;
    }
    this.$currentIndex.update(i => i + 1);
    this.hydrateFromAnswers();
  }

  public goPrev(): void {

    if (this.$disabled() || this.$isFirst()) return;
    this.captureCurrent();
    this.$currentIndex.update(i => i - 1);
    this.hydrateFromAnswers();
  }

  public skipCurrent(): void {

    if (this.$disabled()) return;
    this.markSkipped();
    if (this.$isLast()) {
      this.submitAll();
      return;
    }
    this.$currentIndex.update(i => i + 1);
    this.hydrateFromAnswers();
  }

  public onPrimaryAction(): void {

    if (this.$isLast()) this.submitAll();
    else this.goNext();
  }

  public onCancel(): void {

    if (this.$disabled()) return;
    this.cancel.emit();
  }

  // ─── Capture / hydrate ────────────────────────────────────────────────────

  private captureCurrent(): void {

    const q = this.$currentQuestion();
    const idx = this.$currentIndex();
    let selectedLabels: string[] = [];
    let customText: string | undefined;
    let skipped = false;

    if (q.mode === 'single') {
      const sIdx = this.$selectedIndex();
      if (sIdx !== null) selectedLabels = [q.options[sIdx].label];
      if (this.$freetextSelected() && this.$customText().trim()) customText = this.$customText().trim();
      skipped = selectedLabels.length === 0 && !customText;
    } else if (q.mode === 'multiple') {
      selectedLabels = [...this.$checkedIndices()].sort((a, b) => a - b).map(i => q.options[i].label);
      if (this.$customText().trim()) customText = this.$customText().trim();
      skipped = selectedLabels.length === 0 && !customText;
    } else if (q.mode === 'rank') {
      selectedLabels = this.$rankedOptions().map(o => o.label);
      skipped = selectedLabels.length === 0;
    }

    const next = new Map(this.$answers());
    next.set(idx, { questionIndex: idx, selectedLabels, customText, skipped });
    this.$answers.set(next);
  }

  private markSkipped(): void {

    const idx = this.$currentIndex();
    const next = new Map(this.$answers());
    next.set(idx, { questionIndex: idx, selectedLabels: [], skipped: true });
    this.$answers.set(next);
  }

  private hydrateFromAnswers(): void {

    const q = this.$currentQuestion();
    const idx = this.$currentIndex();
    const prior = this.$answers().get(idx);

    this.$selectedIndex.set(null);
    this.$checkedIndices.set(new Set());
    this.$customText.set('');
    this.$freetextSelected.set(false);
    this.$focusedIndex.set(0);
    this.$rankedOptions.set(q.mode === 'rank' ? [...q.options] : []);

    if (!prior || prior.skipped) {
      this.cardContainerRef()?.nativeElement.focus();
      return;
    }
    if (q.mode === 'single') {
      if (prior.customText) {
        this.$customText.set(prior.customText);
        this.$freetextSelected.set(true);
      } else if (prior.selectedLabels[0]) {
        this.$selectedIndex.set(q.options.findIndex(o => o.label === prior.selectedLabels[0]));
      }
    } else if (q.mode === 'multiple') {
      const checkedSet = new Set<number>();
      prior.selectedLabels.forEach(l => {
        const i = q.options.findIndex(o => o.label === l);
        if (i >= 0) checkedSet.add(i);
      });
      this.$checkedIndices.set(checkedSet);
      if (prior.customText) this.$customText.set(prior.customText);
    } else if (q.mode === 'rank') {
      const labelOrder = new Map(prior.selectedLabels.map((l, i) => [l, i]));
      const ordered = [...q.options].sort((a, b) => (labelOrder.get(a.label) ?? 0) - (labelOrder.get(b.label) ?? 0));
      this.$rankedOptions.set(ordered);
    }
    this.cardContainerRef()?.nativeElement.focus();
  }

  // ─── Submit ───────────────────────────────────────────────────────────────

  private submitAll(): void {

    this.captureCurrent();
    const answers: AIInterviewAnswer[] = this.$data().questions.map((_, i) =>
      this.$answers().get(i) ?? { questionIndex: i, selectedLabels: [], skipped: true },
    );
    const action: CardAction = answers.every(a => a.skipped) ? 'skipped' : 'selected';
    this.submit.emit({ action, answers });
  }

  // ─── Keyboard ─────────────────────────────────────────────────────────────

  @HostListener('keydown', ['$event'])
  protected onKeydown(event: KeyboardEvent): void {

    if (this.$disabled()) return;

    const q = this.$currentQuestion();
    const optionCount = q.options.length;
    const maxIndex = this.$freetextAllowed() ? this.$freetextRowIndex() : optionCount - 1;

    if ((q.mode === 'multiple' || q.mode === 'rank') && event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      if (this.$canSend()) this.onPrimaryAction();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.skipCurrent();
      return;
    }

    if (q.mode === 'rank') return;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        this.$focusedIndex.update(i => i > 0 ? i - 1 : maxIndex);
        this.syncSelectionToFocus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.$focusedIndex.update(i => i < maxIndex ? i + 1 : 0);
        this.syncSelectionToFocus();
        break;
      case 'Enter': {
        if (event.metaKey || event.ctrlKey) return;
        const target = event.target as HTMLElement | null;
        if (target?.tagName === 'INPUT') {
          if (q.mode === 'single' && this.$customText().trim()) {
            event.preventDefault();
            this.$freetextSelected.set(true);
            this.$selectedIndex.set(null);
            this.onPrimaryAction();
          }
          return;
        }
        event.preventDefault();
        const focused = this.$focusedIndex();
        if (q.mode === 'single') {
          if (focused === this.$freetextRowIndex()) {
            if (this.$customText().trim()) {
              this.$freetextSelected.set(true);
              this.$selectedIndex.set(null);
              this.onPrimaryAction();
            }
          } else {
            this.$selectedIndex.set(focused);
            this.$freetextSelected.set(false);
            this.$customText.set('');
            this.onPrimaryAction();
          }
        } else if (q.mode === 'multiple') {
          if (focused < optionCount) this.onCheckboxToggle(focused);
        }
        break;
      }
    }
  }

  /** In single mode, arrow keys auto-select the focused numbered row */
  private syncSelectionToFocus(): void {

    const q = this.$currentQuestion();
    const focused = this.$focusedIndex();
    if (q.mode === 'single' && focused !== this.$freetextRowIndex()) {
      this.$selectedIndex.set(focused);
      this.$freetextSelected.set(false);
      this.$customText.set('');
    }
    this.cardContainerRef()?.nativeElement.focus();
  }
}
